import { ReactNode, createContext, useContext } from 'react'
import { AuthenticationClient } from '~/plugins/authentication/client'
import { useOrganization } from './hooks/useOrganization'
import { useOrganizationMembers } from './hooks/useOrganizationMembers'
import { useOrganizations } from './hooks/useOrganizations'

const useInternal = () => {
  const { session } = AuthenticationClient.use()

  const isAllowed = true

  const organizations = useOrganizations({ ...session, isAllowed })

  const { organizationMember, notificationCounters, ...organization } =
    useOrganization({
      user: session.user,
      isLoggedIn: session.isLoggedIn,
      organizations: organizations.data,
      isAllowed: isAllowed && organizations.isReady,
    })

  const organizationMembers = useOrganizationMembers({
    organization: organization.data,
    isAllowed: isAllowed && organization.isReady,
  })

  const isReady =
    organizations.isReady && organization.isReady && organizationMembers.isReady

  return {
    isReady,
    organizations,
    organization,
    organizationMember,
    organizationMembers,
    notificationCounters,
    session,
  }
}

type OrganizationContext = ReturnType<typeof useInternal>

const OrganizationContext = createContext<OrganizationContext>(undefined)

export const OrganizationProvider = ({ children }: { children: ReactNode }) => {
  const value = useInternal()

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  )
}

export const useOrganizationContext = (): OrganizationContext => {
  const context = useContext(OrganizationContext)
  if (context === undefined) {
    throw new Error(
      'useOrganizationContext must be used within a OrganizationProvider',
    )
  }

  return context
}
